import { TimeSeriesChartPageSection } from 'components/chart/TimeSeriesChartPageSection';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { MeasureCardRow } from 'components/MeasureCardRow';
import { toMarkdownVsCategoryMeasure } from 'components/price/priceMeasures';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { useDataRequest } from 'hooks/useDataRequest';
import { usePriceKpiData, usePriceTimeSeries } from 'pages/entities/queryHooks';
import { Filters } from 'services/data.service';
import { useModelService, Measure } from 'services/model/model.service';
import { createTimeSeriesChartConfig } from 'utils/createTimeSeriesChartConfig';
import { useIntl } from 'react-intl';
import { useState } from 'react';

interface PriceTabProps {
  filterDecorator: (filter: Filters) => Filters;
}

export function PriceTab({ filterDecorator }: PriceTabProps) {
  const intl = useIntl();
  const labelContext = useDatePickerLabels();
  const { getAttribute, getMeasures } = useModelService();
  const [avgSoldPrice, avgPriceMarkdown, soldAtOriginalPrice] = getMeasures([
    'itemSlsGmvValExtOD',
    'slsMkdRateOD',
    'slsAtOmvQtyRateOD',
  ]);
  const measures = [avgSoldPrice, avgPriceMarkdown, soldAtOriginalPrice, toMarkdownVsCategoryMeasure(avgPriceMarkdown)];
  const requestData = useDataRequest();
  const adjustedRequestData = { ...requestData, filters: filterDecorator(requestData.filters) };
  const [timeSeriesMeasure, setTimeSeriesMeasure] = useState<Measure>(measures[0]);

  const { data: totalsData } = usePriceKpiData(adjustedRequestData);
  const { data: timeSeriesData } = usePriceTimeSeries(adjustedRequestData);

  const timeSeriesRenderer = timeSeriesData
    ? new LineChartRenderer(
        createTimeSeriesChartConfig({
          getAttribute,
          measures: [timeSeriesMeasure],
          request: adjustedRequestData,
          intl,
          response: timeSeriesData,
          labelContext,
        })
      )
    : null;

  const onMeasureSelect = (measure: Measure) => () => {
    if (measure.code !== timeSeriesMeasure.code) {
      setTimeSeriesMeasure(measure);
    }
  };

  return (
    <>
      {totalsData && (
        <MeasureCardRow
          measures={measures}
          totalsData={totalsData}
          selectedMeasureCode={timeSeriesMeasure.code}
          onMeasureSelect={onMeasureSelect}
          testid="price-measure-card-row"
        />
      )}
      <TimeSeriesChartPageSection
        timeSeriesMeasure={timeSeriesMeasure}
        timeSeriesRenderer={timeSeriesRenderer}
        showGroupByToggle={false}
        dataTestId="price"
      />
    </>
  );
}
